<template>
  <form
    role="search"
    @submit.prevent="onFormSubmit"
    :class="[
      'group relative flex gap-3 content-center  rounded-full border p-2 border-gray-300 hover:border-gray-600 focus-within:border-gray-800',
      props.customFormClass
    ]"
    v-click-outside="clearSearch"
  >
    <Icon
      :class="[
        'my-auto text-gray-500 group-focus-within:text-gray-700',
        props.customIconClass
      ]"
      icon="search"
    />

    <BaseInput 
      v-model="searchQuery"
      aria-label="Search for content on Thorne"  
      :placeholder="props.placeholder || 'Search'"  
      type="search" 
      :class="[
        'grow my-auto focus:outline-none bg-white text-gray-800',
        props.customInputClass
      ]"
      :id="id"
      ref="searchInput"  
      @input="onSearchInputChanged"
      @focus="onInputClick"
      @keydown="onSearchKeydown"
    />
     <div role="status" aria-live="polite" class="sr-only">Results available</div>
    <button v-if="searchQuery" type="button" @click="clearSearch" class="absolute right-3 my-auto text-gray-500 hover:text-black focus:outline-none focus:border-2 focus:border-black rounded" aria-label="Clear search">✕</button>
    <div
      role="menu"
      class="flex flex-col absolute top-[102%] left-0 w-full p-[0.857142857rem] drop-shadow-md rounded-md bg-white"
      v-if="searchResults.length > 0"
    >
      <search-result-category
        @clear-search-results="clearSearch"
        :query="searchQuery"
        :search-suggestions="searchResults"
      ></search-result-category>
    </div>
    <div class="sr-only" id="results-suggestions-status" role="status">
      {{ accessibilitySearchResultsAvailable }}
    </div>
  </form>
</template>

<script setup lang="tsx">
import { ref, computed } from 'vue'
import Icon from '../../atom/icon.vue'
import debounce from 'debounce'
import SearchResultCategory from '../../molecule/search/searchResultCategory.vue'
import { useIframeStore } from '~/stores/chatAIStore'
import BaseInput from '../../atom/baseInput.vue'
import { URL_CONFIG } from '~~/shared/utils/url.const.js'
const iframeStore = useIframeStore()
const searchInput = ref<{ inputEl: HTMLInputElement | null } | null>(null)

interface SearchResults {
  q: string
  recommendationContext: {
    type: string
    data: string[]
  }
  searchPaging: {}
  filters: {}
  page: SearchResultsPage
}

interface SearchResultsPage {
  products?: {}
  articles?: {}
  videos?: {}
  resources?: {}
  ingredients?: {}
  podcasts?: {}
  affiliates?: {}
  metaTitle: string
  resultsAvailableFor: SearchResultCategories[]
}

enum SearchResultCategories {
  Products = 'Products',
  Articles = 'Articles',
  Videos = 'Videos',
  Resources = 'Resources',
  Ingredients = 'Ingredients',
  Podcasts = 'Podcast',
  Affiliates = 'Affiliates'
}

interface SearchBarProps {
  placeholder?: string
  customIconClass?: string
  customInputClass?: string
  customFormClass?: string
  blogPage?: boolean
  searchValue?: string
  id?: string
}

const props = withDefaults(defineProps<SearchBarProps>(), {
  blogPage: false,
  searchValue: '',
  id:''
})

const searchQuery = ref<string>(props.blogPage ? props.searchValue || '' : '')
const searchResults = ref<string[]>([])
const accessibilitySearchResultsAvailable = computed(() =>
  searchResults.value.length > 0 ? 'Suggestions available' : ''
)

const onFormSubmit = () => {
  if (searchQuery.value.trim() === '') {
    return;
  }

  if(props.blogPage) {
    const encoded = encodeURIComponent(searchQuery.value)
    navigateTo(`${URL_CONFIG.TAKE_5_DAILY_SEARCH}=${encoded}`,{ external: true })
  } else {
    navigateTo(`${URL_CONFIG.GLOBAL_SEARCH}=${searchQuery.value}`,{ external: true })
  } 
};

const onSearchInputChanged = debounce((e: Event) => {
  if (!(e.target instanceof HTMLInputElement)) return

  const target: HTMLInputElement = e.target
  const value: string = target.value
  if (!props.blogPage && value != null && value.length > 0) {
    searchQuery.value = value
    iframeStore.updateInputSearchQuery(searchQuery.value)
    fetchSearchResults(value)
  }
}, 500)

const onInputClick = () => {
  iframeStore.sendMessageToChatbot('minimise', {
    currentMinimise: true
  })
}

const fetchSearchResults = (query: string) => {
  searchResults.value = []
  if (query.length <= 0) return

  $fetch<SearchResults>('/search', {
    params: {
      q: query,
      format: 'json'
    }
  })
    .then((results: SearchResults) => {
      if (results) {
        searchResults.value = results.page.resultsAvailableFor
      }
    })
    .catch(err => {})
}

const clearSearch = () => {
  !props.blogPage && (searchQuery.value = '')
  searchResults.value = []
  searchInput.value?.inputEl?.blur()
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    clearSearch()
    setTimeout(() => {
      searchInput.value?.inputEl?.blur()
    }, 0)
  }
}

</script>
<style>
input[type="search"]::-webkit-search-cancel-button {
  -webkit-appearance: none;
  display: none;
}

</style>
